/**
 * @fileoverview Section "Cards" — composant shadcn `<Card>` avec ses parts
 * (Header, Title, Description, Action, Content, Footer) et une preview de
 * `MetricCard`, `PageHeader` et `SectionHeader`.
 */

import {
  Button,
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  MetricCard,
  PageHeader,
  SectionHeader,
  Timeline,
} from '@gnpc/ui';
import { AlertTriangle, ArrowUpRight, TrendingDown, TrendingUp } from 'lucide-react';
import { DsPreview } from '../components/DsPreview.tsx';

export function DsSectionCards(): JSX.Element {
  return (
    <div className="space-y-8">
      <div className="space-y-3">
        <h3 className="text-quipux-navy-950 text-sm font-semibold">Card générique</h3>
        <div className="grid gap-4 lg:grid-cols-2">
          <DsPreview label="Card minimale — content only">
            <Card className="w-full max-w-sm">
              <CardContent>
                <p className="text-quipux-navy-800 text-sm">
                  Contenu simple. Utiliser <code>Card + CardContent</code> pour tout ce qui n'a pas
                  besoin de header.
                </p>
              </CardContent>
            </Card>
          </DsPreview>

          <DsPreview label="Card complète — header + description + action + footer">
            <Card className="w-full max-w-sm">
              <CardHeader>
                <CardTitle>Paiement PAY-2947</CardTitle>
                <CardDescription>Encaissé le 14 juillet 2026</CardDescription>
                <CardAction>
                  <Button size="sm" variant="ghost">
                    Détail
                  </Button>
                </CardAction>
              </CardHeader>
              <CardContent>
                <p className="text-quipux-navy-800 text-sm">
                  3 lignes de liquidation, total 45 000 F CFA. Enregistrement STI en cours.
                </p>
              </CardContent>
              <CardFooter className="justify-end gap-2 border-t">
                <Button variant="outline" size="sm">
                  Historique
                </Button>
                <Button size="sm">Rejouer STI</Button>
              </CardFooter>
            </Card>
          </DsPreview>
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="text-quipux-navy-950 text-sm font-semibold">Headers métier et MetricCard</h3>
        <p className="text-muted-foreground text-xs">
          `PageHeader` ouvre les pages console, `SectionHeader` structure les panneaux et
          `MetricCard` porte les métriques opérationnelles.
        </p>

        <DsPreview label="PageHeader + SectionHeader">
          <div className="grid w-full gap-5">
            <PageHeader
              eyebrow="Paiements"
              title="Suivre les paiements"
              description="Consulter les transactions, filtrer les statuts et traiter les anomalies STI."
              actions={<Button size="sm">Exporter</Button>}
            />
            <SectionHeader
              title="Anomalies STI"
              description="Paiements encaissés dont l'enregistrement côté STI nécessite une action."
              action={
                <Button variant="outline" size="sm">
                  Voir la file
                </Button>
              }
            />
          </div>
        </DsPreview>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            label="Paiements aujourd'hui"
            value="4 127"
            icon={<TrendingUp />}
            tone="success"
            trend={{ value: '+6,2 %', direction: 'up', icon: <TrendingUp /> }}
            footer={{
              title: 'Trafic portail en hausse',
              description: 'Transactions initiées par les usagers.',
              icon: <ArrowUpRight />,
            }}
          />
          <MetricCard
            label="Montant collecté"
            value="84,2 M"
            tone="violet"
            trend={{ value: '+8,4 %', direction: 'up', icon: <TrendingUp /> }}
            footer={{ title: 'F CFA collectés', description: 'Rapproché à J-1.' }}
          />
          <MetricCard
            label="Taux de succès (24 h)"
            value="98,4 %"
            trend={{ value: '-1,1 pt', direction: 'down', icon: <TrendingDown /> }}
            footer={{ title: 'Sous surveillance', description: 'SLA cible 99 %.' }}
          />
          <MetricCard
            label="Encaissé non enregistré"
            value="7"
            icon={<AlertTriangle />}
            tone="danger"
            trend={{ value: '+2', direction: 'down', icon: <TrendingDown /> }}
            footer={{
              title: 'Nécessite une action',
              description: 'Paiements encaissés non enregistrés côté STI.',
            }}
          />
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="text-quipux-navy-950 text-sm font-semibold">Timeline métier</h3>
        <p className="text-muted-foreground text-xs">
          `Timeline` affiche les transitions paiement, STI, remboursement ou audit dans un ordre
          chronologique lisible.
        </p>

        <DsPreview label="Timeline — historique d'un paiement">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle>Historique de traitement</CardTitle>
              <CardDescription>Événements visibles par l’agent.</CardDescription>
            </CardHeader>
            <CardContent>
              <Timeline
                items={[
                  {
                    id: 'created',
                    title: 'Paiement créé',
                    timestamp: '14 juil. 2026, 08:22',
                    tone: 'info',
                  },
                  {
                    id: 'confirmed',
                    title: 'Paiement confirmé',
                    timestamp: '14 juil. 2026, 08:27',
                    tone: 'success',
                  },
                  {
                    id: 'sti',
                    title: 'Enregistrement STI à reprendre',
                    timestamp: '14 juil. 2026, 08:27',
                    description:
                      'Le paiement est encaissé, mais certaines lignes doivent être relancées.',
                    tone: 'danger',
                  },
                ]}
              />
            </CardContent>
          </Card>
        </DsPreview>
      </div>
    </div>
  );
}
